import { useQuery } from '@tanstack/react-query';
import zeusSdk from '_/utils/SDKs';
import React, { createContext, useContext, ReactNode } from 'react';

interface SystemAlert {
  outage?: boolean;
  message?: string;
  title?: string;
}

interface SystemAlertContextValue {
  systemAlert: SystemAlert;
  isLoading: boolean;
  refetch: () => void;
}

const SystemAlertContext = createContext<SystemAlertContextValue | undefined>(undefined);

export function SystemAlertProvider({ children }: { children: ReactNode }) {
  const {
    data: systemAlert,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['systemAlert'],
    queryFn: async () => {
      try {
        // Skip fetching on local environment
        if (process.env.ENDPOINTS_WORKBENCH_API_BASE_URL === 'http://local.demo-owl.com:3000/api') {
          return {};
        }
        return await zeusSdk.app.getSystemAlert();
      } catch (error) {
        console.error('Error fetching system alert', error);
        return {};
      }
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchInterval: 60 * 60 * 1000, // Refetch every 1 hour
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 1, // Only retry once on failure
  });

  const value: SystemAlertContextValue = {
    systemAlert: systemAlert || {},
    isLoading: isLoading,
    refetch: refetch,
  };

  return <SystemAlertContext.Provider value={value}>{children}</SystemAlertContext.Provider>;
}

export function useSystemAlert(): SystemAlertContextValue {
  const context = useContext(SystemAlertContext);
  if (context === undefined) {
    throw new Error('useSystemAlert must be used within a SystemAlertProvider');
  }
  return context;
}
